import { useState, useEffect } from 'react';

/**
 * Hook to detect media query matches
 * @param query - Media query string (e.g., '(min-width: 1280px)')
 * @returns boolean indicating if the media query matches
 */
export function useMediaQuery(query: string): boolean {
  // SSR-safe initialization
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false; // Default to false on server
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // Skip if SSR
    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia(query);

    // Update to current value (in case it changed during SSR)
    setMatches(media.matches);

    // Define event handler
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add event listener
    media.addEventListener('change', handler);

    // Cleanup
    return () => {
      media.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}

// Tailwind breakpoint helpers
export const useBreakpoint = () => {
  const isSm = useMediaQuery('(min-width: 640px)');
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const isXl = useMediaQuery('(min-width: 1280px)');
  const is2xl = useMediaQuery('(min-width: 1536px)');

  return {
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,
    // Inverse helpers
    isMobile: !isSm,
    isTablet: isSm && !isLg,
    isDesktop: isLg,
  };
};
